import React from 'react';
import {
    Box,
    Card,
    CardContent,
    Typography,
    Button,
    Chip,
    ToggleButton,
    ToggleButtonGroup,
} from '@mui/material';
import {
    Add as AddIcon,
    Visibility as ViewIcon,
    PlayArrow as ProcessIcon,
    LocalShipping as ShipIcon,
    CheckCircle as DeliverIcon,
    Cancel as CancelIcon,
    Payment as PayIcon,
    Edit as EditIcon,
    CheckCircleOutline as ConfirmIcon,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { useTranslation } from 'react-i18next';
import orderService from '../../../services/orderService';
import AppDataGrid from '../../common/AppDataGrid';
import GridActionsCell from '../../common/GridActionsCell';
import TableSearch from '../../common/TableSearch';
import DeliveryChip from '../../common/DeliveryChip';
import { useDeliveryCompanies } from '../../../hooks/useDeliveryCompanies';
import { useDataTable } from '../../../hooks/useDataTable';
import { useCurrency } from '../../../hooks/useCurrency';

const STATUS_COLORS = {
    DRAFT: 'default',
    CONFIRMED: 'success',
    PENDING: 'warning',
    PROCESSING: 'info',
    SHIPPED: 'primary',
    DELIVERED: 'success',
    CANCELLED: 'error',
    RETURN_REQUESTED: 'warning',
    EXCHANGE_REQUESTED: 'warning',
    RETURNED: 'secondary',
    EXCHANGED: 'secondary',
};

const PAYMENT_STATUS_COLORS = {
    PENDING: 'warning',
    PAID: 'success',
    FAILED: 'error',
    REFUNDED: 'default',
};

const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('fr-FR', { year: 'numeric', month: 'short', day: 'numeric' });
};

const OrdersList = () => {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { format } = useCurrency();
    const { companies } = useDeliveryCompanies();

    // Page, size, search and sort live in the URL (/orders?page=0&size=20&search=...&sort=orderedAt,desc).
    const table = useDataTable(orderService.getOrdersPage, {}, {
        onError: () => toast.error(t('orders.fetchError')),
    });

    const handleStatusUpdate = async (order, status) => {
        try {
            await orderService.updateStatus(order.id, status);
            toast.success(t('orders.statusSuccess'));
            table.refresh();
        } catch (err) {
            toast.error(err.response?.data?.message || t('common.saveError'));
        }
    };

    const handleMarkPaid = async (order) => {
        try {
            await orderService.updatePaymentStatus(order.id, 'PAID');
            toast.success(t('orders.paymentSuccess'));
            table.refresh();
        } catch (err) {
            toast.error(err.response?.data?.message || t('common.saveError'));
        }
    };

    // ADMIN-source orders skip the processing/shipping pipeline entirely (DRAFT -> CONFIRMED or
    // CANCELLED, then CONFIRMED is terminal); WEBSITE orders keep the original
    // PENDING -> PROCESSING -> SHIPPED -> DELIVERED flow. Mirrors OrderDetails.jsx.
    const buildActions = (order) => {
        const isAdminOrder = order.source === 'ADMIN';
        const canTransitionStatus = isAdminOrder
            ? order.status === 'DRAFT'
            : ['PENDING', 'PROCESSING', 'SHIPPED'].includes(order.status);

        return [
            {
                key: 'view',
                icon: ViewIcon,
                title: t('common.details'),
                color: 'info',
                onClick: () => navigate(`/orders/${order.id}`),
            },
            {
                key: 'edit-draft',
                icon: EditIcon,
                title: t('orders.editDraft'),
                color: 'primary',
                hidden: !(isAdminOrder && order.status === 'DRAFT'),
                onClick: () => navigate(`/orders/${order.id}/edit`),
            },
            {
                key: 'confirm-draft',
                icon: ConfirmIcon,
                title: t('orders.confirmDraft'),
                color: 'success',
                hidden: !(isAdminOrder && order.status === 'DRAFT'),
                onClick: () => handleStatusUpdate(order, 'CONFIRMED'),
            },
            {
                key: 'process',
                icon: ProcessIcon,
                title: t('orders.status.PROCESSING'),
                color: 'primary',
                hidden: !(!isAdminOrder && order.status === 'PENDING'),
                onClick: () => handleStatusUpdate(order, 'PROCESSING'),
            },
            {
                key: 'ship',
                icon: ShipIcon,
                title: t('orders.status.SHIPPED'),
                color: 'primary',
                hidden: !(!isAdminOrder && order.status === 'PROCESSING'),
                onClick: () => handleStatusUpdate(order, 'SHIPPED'),
            },
            {
                key: 'deliver',
                icon: DeliverIcon,
                title: t('orders.status.DELIVERED'),
                color: 'success',
                hidden: !(!isAdminOrder && order.status === 'SHIPPED'),
                onClick: () => handleStatusUpdate(order, 'DELIVERED'),
            },
            {
                key: 'pay',
                icon: PayIcon,
                title: t('orders.paymentStatuses.PAID'),
                color: 'success',
                hidden: !(order.paymentStatus === 'PENDING' && order.status !== 'CANCELLED'),
                onClick: () => handleMarkPaid(order),
            },
            {
                key: 'cancel',
                icon: CancelIcon,
                title: t('orders.status.CANCELLED'),
                color: 'error',
                hidden: !canTransitionStatus,
                onClick: () => handleStatusUpdate(order, 'CANCELLED'),
            },
        ];
    };

    const columns = [
        {
            field: 'orderNumber',
            headerName: t('orders.orderNumber'),
            width: 190,
            cellRenderer: (params) => (
                <Typography variant="body2" fontWeight="medium" sx={{ lineHeight: 'inherit' }}>
                    {params.value}
                </Typography>
            ),
        },
        {
            field: 'customerName',
            headerName: t('orders.customer'),
            flex: 1,
            minWidth: 160,
            cellRenderer: (params) => params.value || <em>{t('orders.walkIn')}</em>,
        },
        {
            field: 'status',
            headerName: t('common.status'),
            width: 170,
            cellRenderer: (params) => (
                <Chip
                    label={t(`orders.status.${params.value}`)}
                    color={STATUS_COLORS[params.value] || 'default'}
                    size="small"
                />
            ),
            tooltipValueGetter: (params) => t(`orders.status.${params.value}`),
        },
        {
            field: 'paymentStatus',
            headerName: t('orders.paymentStatus'),
            width: 150,
            cellRenderer: (params) => (
                <Chip
                    label={t(`orders.paymentStatuses.${params.value}`)}
                    color={PAYMENT_STATUS_COLORS[params.value] || 'default'}
                    size="small"
                    variant="outlined"
                />
            ),
            tooltipValueGetter: (params) => t(`orders.paymentStatuses.${params.value}`),
        },
        {
            field: 'deliveryCompany',
            headerName: t('delivery.deliveryColumn'),
            width: 150,
            sortable: false,
            cellRenderer: (params) => (params.data.deliveryMethod ? <DeliveryChip order={params.data} /> : '—'),
            tooltipValueGetter: () => null,
        },
        { field: 'paymentMethod', headerName: t('orders.paymentMethod'), width: 150, valueFormatter: (p) => p.value || '-' },
        {
            field: 'totalAmount',
            headerName: t('orders.totalAmount'),
            width: 150,
            cellRenderer: (params) => (
                <Typography variant="body2" fontWeight="medium" sx={{ lineHeight: 'inherit' }}>
                    {format(params.value)}
                </Typography>
            ),
            tooltipValueGetter: (params) => format(params.value),
        },
        {
            field: 'orderedAt',
            headerName: t('common.date'),
            width: 140,
            cellRenderer: (params) => formatDate(params.value),
            tooltipValueGetter: (params) => formatDate(params.value),
        },
        {
            field: 'actions',
            headerName: t('common.actions'),
            width: 220,
            sortable: false,
            cellRenderer: (params) => <GridActionsCell actions={buildActions(params.data)} />,
        },
    ];

    return (
        <Box sx={{ p: 3 }}>
            {/* Header */}
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h4">{t('orders.title')}</Typography>
                <Button variant="contained" startIcon={<AddIcon />} onClick={() => navigate('/orders/create')}>
                    {t('orders.createOrder')}
                </Button>
            </Box>

            <Card>
                <CardContent>
                    <TableSearch
                        placeholder={t('orders.searchPlaceholder')}
                        value={table.searchInput}
                        onChange={table.handleSearchChange}
                        sx={{ mb: 2 }}
                    />

                    <ToggleButtonGroup
                        size="small"
                        exclusive
                        value={table.params.deliveryCompany || ''}
                        onChange={(_, next) => next !== null && table.updateParams({ deliveryCompany: next })}
                        sx={{ mb: 2 }}
                        aria-label={t('delivery.filterLabel')}
                    >
                        <ToggleButton value="">{t('delivery.filterAll')}</ToggleButton>
                        {companies.map((c) => (
                            <ToggleButton key={c.code} value={c.code}>{c.name}</ToggleButton>
                        ))}
                        <ToggleButton value="HOME">{t('delivery.home')}</ToggleButton>
                    </ToggleButtonGroup>

                    <AppDataGrid
                        rows={table.data.content}
                        columns={columns}
                        loading={table.loading}
                        paginationMode="server"
                        rowCount={table.data.totalElements}
                        paginationModel={table.paginationModel}
                        onPaginationModelChange={table.handlePaginationModelChange}
                        sortModel={table.sortModel}
                        onSortModelChange={table.handleSortChange}
                        pageSizeOptions={[10, 20, 50, 100]}
                        getRowId={(row) => row.id}
                        onRowClicked={(e) => {
                            // Clicking the row (but not an action icon) opens the order, like the old table did.
                            if (!e.event?.target?.closest?.('button')) navigate(`/orders/${e.data.id}`);
                        }}
                    />
                </CardContent>
            </Card>
        </Box>
    );
};

export default OrdersList;
